import React, { useState } from 'react';
import { Mail, Phone, MapPin, Instagram, MessageSquare } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../utils/translations';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { submitContactForm } from '../services/api';
import ConfirmationDialog from './ConfirmationDialog';

const ContactSection = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await submitContactForm({ ...formData, language });
      setShowConfirmation(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast({
        title: t.error,
        description: error.message || (language === 'en' ? 'Something went wrong. Please try again.' : 'حدث خطأ ما. يرجى المحاولة مرة أخرى.'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const whatsappMessage = language === 'en'
    ? 'Hello! I\'m interested in learning more about NuzhaTrips.xyz subscription plans.'
    : 'مرحباً! أرغب في معرفة المزيد عن خطط اشتراك NuzhaTrips.xyz.';
  
  const whatsappUrl = `https://wa.me/96879008872?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-16">{t.getInTouch}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-bold mb-6">{t.contactInfo}</h3>
            
            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="bg-orange-100 p-3 rounded-full flex-shrink-0">
                  <Mail className="text-orange-500 w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold mb-1">{t.email}</h4>
                  <a href="mailto:Nuzha@NuzhaTrips.xyz" className="text-gray-600 hover:text-orange-500 transition-colors">
                    Nuzha@NuzhaTrips.xyz
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="bg-purple-100 p-3 rounded-full flex-shrink-0">
                  <MessageSquare className="text-purple-500 w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold mb-1">{t.whatsapp}</h4>
                  <a 
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-purple-500 transition-colors"
                    dir="ltr"
                    style={{ unicodeBidi: 'embed' }}
                  >
                    +968 7900 8872
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="bg-orange-100 p-3 rounded-full flex-shrink-0">
                  <MapPin className="text-orange-500 w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold mb-1">{t.location}</h4>
                  <p className="text-gray-600">{t.locationValue}</p>
                </div>
              </div>
            </div>
            
            <h3 className="text-xl font-bold mb-4">{t.followUs}</h3>
            <div className="flex gap-4">
              <a 
                href="https://www.instagram.com/nuzhatrips?igsh=MWk5Z291enlwNHliaA==" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-gray-100 hover:bg-gray-200 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Instagram className="text-gray-600 w-5 h-5" />
              </a>
              <a 
                href={whatsappUrl}
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-gray-100 hover:bg-gray-200 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <MessageSquare className="text-gray-600 w-5 h-5" />
              </a>
            </div>
          </div>
          
          {/* Contact Form */}
          <div>
            <h3 className="text-2xl font-bold mb-6">{t.sendMessage}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">{t.firstName}</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">{t.lastName}</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="email">{t.email}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="subject">{t.subject}</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="message">{t.message}</Label>
                <Textarea
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                />
              </div>
              
              <Button 
                type="submit" 
                className="bg-orange-500 hover:bg-orange-600 w-full md:w-auto px-8 py-6"
                disabled={loading}
              >
                {loading ? t.loading : t.sendMessageBtn}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        type="contact"
      />
    </section>
  );
};

export default ContactSection;
